/**
 * Bestand übernehmen (0064): laufende Kooperationen und offene Rechnungen aus dem bisherigen System (Excel, CSV, Export
 * eines anderen Werkzeugs). Reine Funktionen: Werte lesen (Zahlen 1.234,56 und 1234.56, Datum 25.09.2026 oder 2026-09-25,
 * Excel-Datumszahlen), Spalten erkennen, Creator, Marken, Modelle, Stände und Kooperationen zuordnen, je Zeile prüfen und
 * Dubletten markieren. Angelegt wird danach über die Wege der Datenbank (deal_save, deal_import_finish, invoice_import_legacy).
 */
import {
  addDaysByUnit, allocateEvenly, DEAL_MODEL_KEYS, DEAL_MODEL_NAME, determineTaxTreatment, dunningStageLabel, dunningStageOrdinalLabel, forcedTaxTreatment, holidayDates,
  normalizeInvoiceNumber, parseAmountToCents, taxModeLabel, TAX_MODE_DB,
  type Currency, type DealModelKey, type DunningPolicy, type TaxMode,
} from "@talentkontor/domain";
import { monthLabel } from "../../../lib/format";
import { DEAL_STATUS, DEAL_STATUS_ORDER, labelMap } from "../kooperationen/model";
import type { KoopDaten } from "../kooperationen/typen";
import { fold, routeOf, type ImportRow } from "./import-model";
import { cleanText, percentToBp } from "./model";
import { t, currentLang } from "../../../lib/sprache";

// ── Werte lesen ────────────────────────────────────────────────────────────────

/** Excel zählt Tage ab dem 30.12.1899 (mit dem Schaltjahrfehler 1900); 25569 = 01.01.1970. */
function excelSerialToIso(serial: number): string | null {
  if (!Number.isFinite(serial) || serial < 20000 || serial > 80000) return null;
  const d = new Date(Math.round((Math.floor(serial) - 25569) * 86400000));
  return d.toISOString().slice(0, 10);
}
function validIso(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
}
const fullYear = (y: string) => (y.length === 2 ? 2000 + Number(y) : Number(y));

/** Datum → JJJJ-MM-TT. Versteht 25.09.2026, 25.9.26, 2026-09-25, 25/09/2026 und Excel-Datumszahlen. Leer = null, ungültig = undefined. */
export function parseDate(raw: string | null | undefined): string | null | undefined {
  const s = (raw ?? "").trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T].*)?$/.exec(s);
  if (m) return validIso(Number(m[1]), Number(m[2]), Number(m[3])) ?? undefined;
  m = /^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})\.?$/.exec(s);
  if (m) return validIso(fullYear(m[3]!), Number(m[2]), Number(m[1])) ?? undefined;
  if (/^\d{5}(\.\d+)?$/.test(s)) return excelSerialToIso(Number(s)) ?? undefined;
  return undefined;
}

const MONTH_WORDS: Record<string, number> = {
  januar: 1, jan: 1, jaenner: 1, january: 1, februar: 2, feb: 2, february: 2, maerz: 3, mrz: 3, mar: 3, march: 3, april: 4, apr: 4, mai: 5, may: 5,
  juni: 6, jun: 6, june: 6, juli: 7, jul: 7, july: 7, august: 8, aug: 8, september: 9, sep: 9, sept: 9, oktober: 10, okt: 10, oct: 10, october: 10,
  november: 11, nov: 11, dezember: 12, dez: 12, dec: 12, december: 12,
};

/** Monat → JJJJ-MM. Versteht 09.2026, 9/2026, 2026-09, September 2026, Sep 26, ein ganzes Datum und Excel-Datumszahlen. */
export function parseMonth(raw: string | null | undefined): string | null | undefined {
  const s = (raw ?? "").trim();
  if (!s) return null;
  const key = (y: number, mo: number) => (mo >= 1 && mo <= 12 && y >= 1900 && y <= 2200 ? `${y}-${String(mo).padStart(2, "0")}` : undefined);
  let m = /^(\d{4})[-/.](\d{1,2})$/.exec(s);
  if (m) return key(Number(m[1]), Number(m[2]));
  m = /^(\d{1,2})[-/.](\d{4}|\d{2})$/.exec(s);
  if (m) return key(fullYear(m[2]!), Number(m[1]));
  m = /^([A-Za-zÄÖÜäöüß]+)\.?\s*[-/ ]?\s*(\d{4}|\d{2})$/.exec(s);
  if (m) {
    const mo = MONTH_WORDS[fold(m[1]!)];
    return mo ? key(fullYear(m[2]!), mo) : undefined;
  }
  const d = parseDate(s);
  return d ? d.slice(0, 7) : undefined;
}

/**
 * Betrag → Cent. Versteht 1.234,56 · 1234,56 · 1234.56 · 1,234.56 · 1'234.50 · „1.234,56 €“ und Excel-Zahlen mit
 * Rundungsresten (1199.9999999). Leer = null, ungültig oder negativ = undefined.
 */
export function parseMoney(raw: string | null | undefined): number | null | undefined {
  const s = (raw ?? "").trim();
  if (!s) return null;
  if (isDashZero(s)) return 0;
  // Excel liefert Zahlen mit Punkt und bis zu 15 Nachkommastellen; mehr als drei sind nie ein Tausendertrenner
  if (/^-?\d+\.\d{4,}$/.test(s)) {
    const c = Math.round(Number(s) * 100);
    return c >= 0 ? c : undefined;
  }
  try {
    const c = parseAmountToCents(s);
    return c >= 0 ? c : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Excel-Buchhaltungsformat zeigt 0 als „-“ (auch „–“, „€ -“, „- €“, „CHF -“, PRAXIS-R3-9). Nur beim Einfügen aus Tabellen;
 * parseAmountToCents lehnt einen einzelnen Strich weiter ab (Eingabefelder).
 */
export function isDashZero(raw: string): boolean {
  const bare = raw.replace(/[\s\u00a0\u202f]/g, "").replace(/^(S?Fr\.|€|EUR|CHF|USD|GBP|\$|£)/i, "").replace(/(€|EUR|CHF|USD|GBP|\$|£)$/i, "");
  return /^[-–—]{1,2}$/.test(bare);
}

/** Prozent → Basispunkte; Excel-Prozentzellen (0,25 für 25 %) werden erkannt. Leer = null, ungültig = undefined. */
export function parsePercent(raw: string | null | undefined): { bp: number | null | undefined; fromFraction: boolean } {
  const s = (raw ?? "").trim().replace(/\s*%$/, "");
  if (!s) return { bp: null, fromFraction: false };
  const n = Number(s.replace(",", "."));
  if (Number.isFinite(n) && n > 0 && n < 1) return { bp: Math.round(n * 10000), fromFraction: true };
  return { bp: percentToBp(s), fromFraction: false };
}

/** Ganze Zahl (Anzahl Monate, Zahlungsziel in Tagen) */
function parseInteger(raw: string | null | undefined): number | null | undefined {
  const s = (raw ?? "").trim().replace(/\s*(monate?|tage?|days?|months?)$/i, "");
  if (!s) return null;
  const n = Number(s.replace(",", "."));
  return Number.isInteger(n) ? n : undefined;
}

const CURRENCY_ALIASES: Record<string, Currency> = {
  eur: "EUR", euro: "EUR", "€": "EUR", chf: "CHF", fr: "CHF", sfr: "CHF", franken: "CHF", usd: "USD", dollar: "USD", "$": "USD",
  gbp: "GBP", pfund: "GBP", "£": "GBP", sek: "SEK", dkk: "DKK",
};
export function parseCurrency(raw: string | null | undefined): Currency | null | undefined {
  const s = (raw ?? "").trim();
  if (!s) return null;
  return CURRENCY_ALIASES[s.toLowerCase().replace(/\.$/, "")] ?? CURRENCY_ALIASES[fold(s)] ?? undefined;
}

/** Liste aus einer Zelle: Komma, Semikolon, Schrägstrich, Pluszeichen oder „und“ */
const listOf = (v: string | null) => (v ?? "").split(/[,;/|+]| und | and /i).map((x) => x.trim()).filter(Boolean);

// ── Spalten ────────────────────────────────────────────────────────────────────

const KOOP_COLUMNS: Record<string, string[]> = {
  creator: ["creator", "talent", "creatoremail", "creatorname", "influencer", "emaildescreators", "creatormail"],
  brand: ["marke", "brand", "kunde", "markenname", "auftraggeber"],
  title: ["titel", "title", "kooperation", "kooperationstitel", "projekt", "bezeichnung", "kampagnentitel"],
  model: ["modell", "verguetungsmodell", "model", "verguetung", "abrechnungsmodell"],
  status: ["stand", "status", "arbeitsstand", "standderkooperation"],
  currency: ["waehrung", "currency", "wahrung"],
  fee: ["honorar", "honorarnetto", "nettohonorar", "betrag", "betragnetto", "budget", "fee", "verguetungnetto"],
  commission: ["provision", "provisionprozent", "provisioninprozent", "provisionin", "agenturanteil", "commission", "provisionssatz"],
  start: ["beginn", "beginnmonat", "start", "startmonat", "ab", "startdatum", "leistungsmonat", "monat"],
  months: ["anzahlmonate", "monate", "laufzeit", "laufzeitmonate", "dauer", "months"],
  deliverables: ["leistungen", "leistung", "leistungsumfang", "deliverables", "umfang"],
  platforms: ["plattformen", "plattform", "platforms", "kanaele", "kanal", "channels"],
  usage_rights_until: ["nutzungsrechtebis", "rechtebis", "nutzungbis", "usagerightsuntil", "nutzungsrechteende"],
  usage_rights: ["nutzungsrechte", "nutzungsrechteumfang", "usagerights"],
  route: ["abrechnungsweg", "abrechnung", "invoiceroute", "rechnungsweg"],
  content_deadline: ["contentdeadline", "deadline", "abgabe", "abgabetermin", "contentabgabe"],
  past_state: ["standvergangenermonate", "standvergangenemonate", "standdesmonats", "monatsstand", "standdermonate", "abrechnungsstand"],
  invoiced_until: ["abgerechnetbis"],
  brand_paid_until: ["markebezahltbis", "zahlungdermarkebis", "markehatbezahltbis", "brandpaiduntil"],
  paid_until: ["bezahltbis"],
};
const RECHNUNG_COLUMNS: Record<string, string[]> = {
  number: ["nummer", "rechnungsnummer", "rechnungsnr", "renr", "belegnummer", "number", "invoicenumber", "nr"],
  kind: ["art", "rechnungsart", "typ", "richtung"],
  brand: ["marke", "empfaenger", "empfaengermarke", "rechnungsempfaenger", "kunde", "brand"],
  creator: ["creator", "talent", "creatoremail", "creatorname", "influencer"],
  deal: ["kooperation", "kooperationstitel", "titel", "projekt", "deal"],
  revenue_month: ["leistungsmonat", "monat", "leistungszeitraum"],
  issue_date: ["rechnungsdatum", "datum", "belegdatum", "ausgestelltam", "invoicedate"],
  due_date: ["faelligkeit", "faelligam", "faellig", "faelligkeitsdatum", "zahlungsziel", "duedate"],
  net: ["netto", "nettobetrag", "betragnetto", "net"],
  tax: ["steuer", "steuerbetrag", "ust", "mwst", "umsatzsteuer", "vat", "tax"],
  gross: ["brutto", "bruttobetrag", "betragbrutto", "gesamt", "summe", "gross"],
  currency: ["waehrung", "currency", "wahrung"],
  tax_mode: ["steuerfall", "steuerart", "besteuerung", "taxmode"],
  paid: ["bereitsbezahlt", "bereitsgezahlt", "bezahlt", "gezahlt", "teilzahlung", "zahlungseingang", "paid"],
  paid_on: ["bezahltam", "gezahltam", "zahlungsdatum", "paidon"],
  stage: ["mahnstufe", "stufe", "mahnung", "dunning"],
  last_dunning: ["datumderletztenmahnung", "letztemahnung", "letztemahnungam", "gemahntam", "mahndatum"],
  note: ["bemerkung", "notiz", "kommentar", "hinweis", "note"],
};

export function bestandColumnMap(kind: "kooperationen" | "rechnungen", header: readonly string[]): Record<string, number> {
  const known = kind === "kooperationen" ? KOOP_COLUMNS : RECHNUNG_COLUMNS;
  const out: Record<string, number> = {};
  header.forEach((h, i) => {
    const f = fold(h);
    for (const [key, aliases] of Object.entries(known)) if (!(key in out) && aliases.includes(f)) out[key] = i;
  });
  return out;
}

export const BESTAND_COLUMN_LABEL: Record<string, string> = {
  creator: "Creator", get brand() { return t("Marke", "Brand"); }, get title() { return t("Titel", "Title"); }, get model() { return t("Modell", "Model"); }, status: "Stand", currency: "Währung", get fee() { return t("Honorar", "Fee"); }, get commission() { return t("Provision", "Commission"); },
  get start() { return t("Beginn", "Start"); }, get months() { return t("Monate", "Months"); }, get deliverables() { return t("Leistungen", "Services"); }, get platforms() { return t("Plattformen", "Platforms"); }, get usage_rights_until() { return t("Nutzungsrechte bis", "Usage rights until"); },
  get usage_rights() { return t("Nutzungsrechte", "Usage rights"); }, route: "Abrechnungsweg", get content_deadline() { return t("Content-Deadline", "Content deadline"); }, get past_state() { return t("Stand vergangener Monate", "Status of past months"); },
  get invoiced_until() { return t("Abgerechnet bis", "Invoiced until"); }, get brand_paid_until() { return t("Marke bezahlt bis", "Brand paid until"); }, get paid_until() { return t("Bezahlt bis", "Paid until"); },
  get number() { return t("Nummer", "Number"); }, kind: "Art", get deal() { return t("Kooperation", "Deal"); }, get revenue_month() { return t("Leistungsmonat", "Service month"); }, get issue_date() { return t("Rechnungsdatum", "Invoice date"); }, get due_date() { return t("Fälligkeit", "Due date"); },
  get net() { return t("Netto", "Net"); }, get tax() { return t("Steuer", "Tax"); }, get gross() { return t("Brutto", "Gross"); }, get tax_mode() { return t("Steuerfall", "Tax treatment"); }, get paid() { return t("Bereits bezahlt", "Already paid"); }, get paid_on() { return t("Bezahlt am", "Paid on"); }, get stage() { return t("Mahnstufe", "Dunning level"); },
  get last_dunning() { return t("Letzte Mahnung", "Final dunning notice"); }, get note() { return t("Bemerkung", "Remark"); },
};

export const KOOP_TEMPLATE = [
  "Creator;Marke;Titel;Modell;Stand;Währung;Honorar;Provision;Beginn;Monate;Leistungen;Plattformen;Nutzungsrechte bis;Abrechnungsweg;Content-Deadline;Stand vergangener Monate",
  "mara.linden@example.com;Lunava;Frühjahrskampagne;Einmalig;Abgeschlossen;EUR;2.500,00;;03.2026;1;1 Reel und 3 Stories;Instagram;31.12.2026;über die Agentur;20.03.2026;bezahlt",
  "Noa Winter;Velune;Studio-Begleitung;Monatlich;In Produktion;EUR;1.200,00;25;06.2026;6;Je Monat ein Reel;Instagram, TikTok;;;;abgerechnet",
].join("\r\n") + "\r\n";

export const RECHNUNG_TEMPLATE = [
  "Nummer;Art;Marke;Creator;Kooperation;Leistungsmonat;Rechnungsdatum;Fälligkeit;Netto;Steuer;Brutto;Währung;Steuerfall;Bereits bezahlt;Mahnstufe;Letzte Mahnung;Bemerkung",
  "RE-2026-0412;an Marke;Lunava;mara.linden@example.com;Frühjahrskampagne;03.2026;31.03.2026;14.04.2026;2.500,00;475,00;2.975,00;EUR;Regelbesteuerung;1.000,00;1;02.05.2026;Rest per Überweisung zugesagt",
  "ML-2026-03;Creator an Agentur;;mara.linden@example.com;Frühjahrskampagne;03.2026;02.04.2026;16.04.2026;1.750,00;332,50;2.082,50;EUR;Regelbesteuerung;;;;noch auszuzahlen",
  "PR-2026-09;Provisionsrechnung an Creator;;mara.linden@example.com;;08.2026;31.08.2026;14.09.2026;300,00;57,00;357,00;EUR;Regelbesteuerung;;;;direkter Weg",
  "ML-2026-05;Creator an Marke;Lunava;mara.linden@example.com;;08.2026;31.08.2026;14.09.2026;1.000,00;0,00;1.000,00;EUR;Kleinunternehmer;;;;direkter Weg, überwachen",
].join("\r\n") + "\r\n";

/**
 * Eine einzelne offene Rechnung aus Feldern statt Tabelle (PARITAET-10, wie der Mahngenerator des Altportals): dieselben
 * Spalten und Prüfungen wie der Tabellen-Import (rechnungRows), damit Formular und Tabelle nie auseinanderlaufen.
 */
export interface EinzelRechnung {
  kind: LegacyKind; number: string; brand: string; creator: string; issueDate: string; dueDate: string; net: string; tax: string; gross: string;
  currency: string; taxMode: string; paid: string; stage: string; lastDunning: string; note: string;
}
export function einzelRechnungRows(f: EinzelRechnung): string[][] {
  const kindKey = { agency_to_brand: "agencytobrand", talent_to_agency: "talenttoagency", agency_to_talent: "agencytotalent", talent_to_brand: "talenttobrand" }[f.kind];
  return [
    [t("Nummer", "Number"), t("Art", "Type"), t("Marke", "Brand"), "Creator", t("Rechnungsdatum", "Invoice date"), t("Fälligkeit", "Due date"), t("Netto", "Net"), t("Steuer", "Tax"), t("Brutto", "Gross"), t("Währung", "Currency"), t("Steuerfall", "Tax treatment"), t("Bereits bezahlt", "Already paid"), t("Mahnstufe", "Dunning level"), t("Letzte Mahnung", "Final dunning notice"), t("Bemerkung", "Remark")],
    [f.number, kindKey, legacyToBrand(f.kind) ? f.brand : "", f.kind === "agency_to_brand" ? "" : f.creator, f.issueDate, f.dueDate, f.net, f.tax, f.gross, f.currency, f.taxMode,
      f.paid, f.stage, f.stage && f.stage !== "0" ? f.lastDunning : "", f.note],
  ];
}

// ── Zuordnung ──────────────────────────────────────────────────────────────────

export interface TalentLite { id: string; first_name: string; last_name: string | null; display_name?: string | null; email: string | null; status?: string | null; country?: string | null; small_business?: boolean | null; vat_id?: string | null }
export interface BrandLite { id: string; name: string; legal_name?: string | null; country?: string | null; vat_id?: string | null; is_business?: boolean | null }
export interface DealLite { id: string; talent_id: string; brand_id: string; title: string; currency: string; model?: string; archived_at?: string | null }
export interface PeriodLite { id: string; deal_id: string; month: string; kind?: string | null; status?: string | null }

export const talentFullName = (t: Pick<TalentLite, "first_name" | "last_name">) => `${t.first_name} ${t.last_name ?? ""}`.trim();

/** Creator per E-Mail oder Name (auch Anzeigename). Mehrdeutige Namen verlangen die E-Mail. */
export function findTalent(raw: string, talents: readonly TalentLite[]): { talent: TalentLite | null; error: string | null } {
  const v = raw.trim();
  const mail = v.toLowerCase();
  if (v.includes("@")) {
    const hit = talents.find((t) => (t.email ?? "").toLowerCase() === mail);
    return hit ? { talent: hit, error: null } : { talent: null, error: t(`Creator „${v}“ nicht gefunden (bitte zuerst unter Creator anlegen oder importieren)`, `Creator “${v}” not found (please create or import under Creators first)`) };
  }
  const f = fold(v);
  const hits = talents.filter((t) => fold(talentFullName(t)) === f || (!!t.display_name && fold(t.display_name) === f));
  if (hits.length > 1) return { talent: null, error: t(`Creator „${v}“ ist mehrdeutig; bitte die E-Mail angeben`, `Creator “${v}” is ambiguous; please enter the email`) };
  return hits[0] ? { talent: hits[0], error: null } : { talent: null, error: t(`Creator „${v}“ nicht gefunden (bitte zuerst unter Creator anlegen oder importieren)`, `Creator “${v}” not found (please create or import under Creators first)`) };
}
export function findBrand(raw: string, brands: readonly BrandLite[]): BrandLite | null {
  const f = fold(raw);
  return brands.find((b) => fold(b.name) === f) ?? brands.find((b) => !!b.legal_name && fold(b.legal_name) === f) ?? null;
}

const MODEL_ALIASES: Record<string, DealModelKey> = {
  einmal: "fixed", einmalig: "fixed", fix: "fixed", festbetrag: "fixed", pauschal: "fixed", pauschale: "fixed", festpreis: "fixed", festbudget: "fixed",
  monatlich: "monthly", monat: "monthly", retainer: "monthly", monatspauschale: "monthly", abo: "monthly",
  raten: "installments", ratenzahlung: "installments", inraten: "installments",
  provision: "commission_only", nurprovision: "commission_only", reineprovision: "commission_only", affiliate: "commission_only",
  festprovision: "fixed_plus_commission", festundprovision: "fixed_plus_commission", fixplusprovision: "fixed_plus_commission", hybrid: "fixed_plus_commission",
  individuell: "custom", custom: "custom", sonstiges: "custom",
  barter: "barter", tausch: "barter", produkttausch: "barter", event: "event", veranstaltung: "event", gifting: "gifting", geschenk: "gifting", prsample: "gifting",
};
/** Modell aus dem Katalog: Schlüssel, deutscher oder englischer Name, gängige Umschreibungen */
export function parseModel(raw: string | null): DealModelKey | null | undefined {
  if (raw === null || !raw.trim()) return null;
  const f = fold(raw);
  for (const k of DEAL_MODEL_KEYS) {
    const n = DEAL_MODEL_NAME[k];
    if ([k, n.de, n.en, n.titleDe, n.titleEn].some((x) => fold(x) === f)) return k;
  }
  return MODEL_ALIASES[f];
}

const STATUS_ALIASES: Record<string, string> = {
  planung: "planning", geplant: "planning", neu: "planning", anfrage: "planning", vertrag: "contract", vertragzurunterschrift: "contract",
  konzept: "concept", produktion: "production", inproduktion: "production", laufend: "production", aktiv: "production", freigabe: "content_approval",
  contentfreigabe: "content_approval", veroeffentlicht: "published", live: "published", online: "published", reporting: "reporting", auswertung: "reporting",
  abrechnung: "invoicing", inabrechnung: "invoicing", abgeschlossen: "done", erledigt: "done", fertig: "done", beendet: "done",
  blockiert: "blocked", pausiert: "blocked", storniert: "cancelled", abgesagt: "cancelled",
};
/** Stand (deal_status) aus der deutschen Bezeichnung, der eigenen Bezeichnung der Agentur oder dem Schlüssel */
export function parseDealStatus(raw: string | null, labels: ReadonlyMap<string, string> = new Map()): string | null | undefined {
  if (raw === null || !raw.trim()) return null;
  const f = fold(raw);
  for (const k of DEAL_STATUS_ORDER) {
    if (fold(k) === f || fold(DEAL_STATUS[k].label) === f || (labels.get(k) && fold(labels.get(k)!) === f)) return k;
  }
  return STATUS_ALIASES[f];
}

/** Stand vergangener Monate: offen, abgerechnet, bezahlt */
export function parseMonthState(raw: string | null): "open" | "invoiced" | "brand_paid" | "paid" | null | undefined {
  if (raw === null || !raw.trim()) return null;
  const f = fold(raw);
  if (["offen", "open", "nein", "keiner", "0"].includes(f)) return "open";
  // Marke hat gezahlt, Creator noch nicht ausgezahlt (PRAXIS-R3-2)
  if (["markebezahlt", "markehatbezahlt", "markehatgezahlt", "brandpaid", "zahlungdermarke", "creatoroffen", "auszahlungoffen", "creatornochnichtausgezahlt"].includes(f)) return "brand_paid";
  if (["abgerechnet", "invoiced", "berechnet", "inrechnunggestellt", "rechnunggestellt", "fakturiert", "gestellt"].includes(f)) return "invoiced";
  if (["bezahlt", "paid", "erledigt", "abgeschlossen", "gebucht", "ausgezahlt", "beglichen"].includes(f)) return "paid";
  return undefined;
}

// ── Kooperationen ──────────────────────────────────────────────────────────────

export interface KoopContext {
  /** Heute in der Zeitzone der Agentur (JJJJ-MM-TT) */
  today: string;
  orgCurrency: string;
  /** erlaubte Währungen der Agentur (leer = nur die Hauswährung) */
  currencies: readonly string[];
  talents: readonly TalentLite[];
  brands: readonly BrandLite[];
  deals: readonly DealLite[];
  /** freigeschaltete Vergütungsmodelle (leer = alle) */
  models: readonly string[];
  /** eigene Bezeichnungen der Stände */
  labels?: ReadonlyMap<string, string>;
  platforms: readonly { key: string; label_de: string; label_en?: string | null; active?: boolean }[];
  /** Standard-Agenturgebühr (wird beim Anlegen eingefroren wie im Formular) */
  fee: { mode: string | null; bp: number | null; cents: number | null };
  /** Recht „Zahlungen bestätigen“ (Monate als bezahlt übernehmen) */
  canConfirmPayments: boolean;
  /**
   * Studio (Creator-Konto): das eigene Profil. Dann ist die Spalte Creator nicht Pflicht (leer = eigenes Profil), und
   * Provision, Abrechnungsweg und Agenturgebühr werden ignoriert; ein Creator-Konto hat keine (die Datenbank setzt sie
   * ohnehin auf 0 bzw. leer, app.guard_creator_org_deal aus 0178).
   */
  selfTalent?: TalentLite | null;
}

/** Hinweis in der Vorschau, wenn eine Spalte im Creator-Konto nicht gilt */
export const imCreatorKontoIgnoriert = (spalte: string) => t(`${spalte} wird im Creator-Konto ignoriert`, `${spalte} is ignored in a creator account`);

export interface KoopImport {
  /** p_values für deal_save (ohne org_id); brand_id fehlt bei neuer Marke */
  deal: Record<string, unknown>;
  terms: { agency_fee_mode: string; agency_fee_bp: number | null; agency_fee_cents: number | null };
  months: { month: string; amount_cents: number | null; commission_forecast_cents: null; content_deadline: string | null }[];
  newBrand: string | null;
  /** vergangene Monate aus dem bisherigen System (deal_import_finish) */
  states: { month: string; state: "invoiced" | "brand_paid" | "paid" }[];
  talentName: string;
  brandName: string;
}

const NO_MONEY = new Set(["event", "gifting"]);
const SINGLE = new Set(["fixed", "barter", "event", "gifting"]);
const MAX_MONTHS = 36;
function addMonth(key: string, n: number): string {
  const [y, m] = key.split("-").map(Number);
  const t = y! * 12 + (m! - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

export function koopRows(rows: readonly string[][], ctx: KoopContext): { map: Record<string, number>; rows: ImportRow<KoopImport>[]; newBrands: string[] } {
  const [header = [], ...body] = rows;
  const map = bestandColumnMap("kooperationen", header);
  const currentMonth = ctx.today.slice(0, 7);
  const allowedCur = ctx.currencies.length ? ctx.currencies : [ctx.orgCurrency];
  const seen = new Set(ctx.deals.filter((d) => !d.archived_at).map((d) => `${d.talent_id}|${d.brand_id}|${fold(d.title)}`));
  const newBrands = new Map<string, string>();
  const out = body.map((r, i): ImportRow<KoopImport> => {
    const get = (k: string) => (map[k] === undefined ? null : cleanText(r[map[k]!]));
    const errors: string[] = [];
    const notes: string[] = [];
    const line = i + 2;

    const title = get("title");
    if (!title) errors.push(t("Titel fehlt", "Title missing"));
    else if (title.length > 200) errors.push(t("Titel ist länger als 200 Zeichen", "Title is longer than 200 characters"));

    let talent: TalentLite | null = null;
    const creatorRaw = get("creator");
    const studio = !!ctx.selfTalent;
    if (!creatorRaw && studio) talent = ctx.selfTalent!;
    else if (!creatorRaw) errors.push(t("Creator fehlt", "Creator missing"));
    else {
      const tl = findTalent(creatorRaw, ctx.talents);
      if (tl.error) errors.push(tl.error);
      else if (tl.talent?.status === "archived") errors.push(t(`Creator „${creatorRaw}“ ist archiviert; erst im Creator-Profil wiederherstellen`, `Creator “${creatorRaw}” is archived; restore them in the creator profile first`));
      else talent = tl.talent;
    }

    let brand: BrandLite | null = null;
    let newBrand: string | null = null;
    const brandRaw = get("brand");
    if (!brandRaw) errors.push(t("Marke fehlt", "Brand missing"));
    else {
      brand = findBrand(brandRaw, ctx.brands);
      if (!brand) {
        newBrand = newBrands.get(fold(brandRaw)) ?? brandRaw;
        newBrands.set(fold(brandRaw), newBrand);
      }
    }

    const model = parseModel(get("model"));
    if (model === undefined) errors.push(t(`Modell „${get("model")}“ unbekannt (${DEAL_MODEL_KEYS.map((k) => DEAL_MODEL_NAME[k].de).join(", ")})`, `Model “${get("model")}” unknown (${DEAL_MODEL_KEYS.map((k) => DEAL_MODEL_NAME[k].de).join(", ")})`));
    else if (model === null) errors.push(t("Modell fehlt", "Model missing"));
    else if (ctx.models.length && !ctx.models.includes(model)) errors.push(t(`Modell „${DEAL_MODEL_NAME[model].de}“ ist für diese Agentur nicht freigeschaltet (Einstellungen · Konditionen)`, `Model “${DEAL_MODEL_NAME[model].de}” is not enabled for this agency (Settings · Terms)`));

    const cur = parseCurrency(get("currency")) ?? (get("currency") ? undefined : (ctx.orgCurrency as Currency));
    if (cur === undefined) errors.push(t(`Währung „${get("currency")}“ unbekannt`, `Currency “${get("currency")}” unknown`));
    else if (!allowedCur.includes(cur)) errors.push(t(`Währung ${cur} ist in dieser Agentur nicht eingerichtet (${allowedCur.join(", ")})`, `Currency ${cur} is not set up in this agency (${allowedCur.join(", ")})`));

    const fee = parseMoney(get("fee"));
    if (fee === undefined) errors.push(t(`Honorar „${get("fee")}“ ist kein gültiger Betrag`, `Fee “${get("fee")}” is not a valid amount`));
    // Studio: Provision gibt es im Creator-Konto nicht; ein Wert wird nicht gelesen, nur genannt
    if (studio && get("commission")) notes.push(imCreatorKontoIgnoriert(t("Provision", "Commission")));
    const pct = studio ? { bp: null, fromFraction: false } : parsePercent(get("commission"));
    if (pct.bp === undefined) errors.push(t("Provision in Prozent zwischen 0 und 100", "Commission in percent between 0 and 100"));
    if (pct.fromFraction && pct.bp != null) notes.push(t(`Provision als ${pct.bp / 100} % gelesen`, `Commission read as ${pct.bp / 100} %`));

    const start = parseMonth(get("start"));
    if (start === undefined) errors.push(t(`Beginn „${get("start")}“ ist kein Monat (MM.JJJJ)`, `Start “${get("start")}” is not a month (MM.YYYY)`));
    else if (start === null) errors.push(t("Beginn fehlt", "Start missing"));
    const n = parseInteger(get("months"));
    if (n === undefined || (n !== null && (n < 1 || n > MAX_MONTHS))) errors.push(t(`Monate: eine Zahl von 1 bis ${MAX_MONTHS}`, `Months: a number from 1 to ${MAX_MONTHS}`));

    const usageUntil = parseDate(get("usage_rights_until"));
    if (usageUntil === undefined) errors.push(t(`Nutzungsrechte bis „${get("usage_rights_until")}“ ist kein Datum`, `Usage rights until “${get("usage_rights_until")}” is not a date`));
    const deadline = parseDate(get("content_deadline"));
    if (deadline === undefined) errors.push(t(`Content-Deadline „${get("content_deadline")}“ ist kein Datum`, `Content deadline “${get("content_deadline")}” is not a date`));
    if (studio && get("route")) notes.push(imCreatorKontoIgnoriert(t("Abrechnungsweg", "Invoicing route")));
    const route = studio ? null : routeOf(get("route"), errors);

    // Plattformen aus dem Katalog der Agentur (Schlüssel oder Bezeichnung)
    const platforms: string[] = [];
    for (const p of listOf(get("platforms"))) {
      const f = fold(p);
      const hit = ctx.platforms.find((c) => fold(c.key) === f || fold(c.label_de) === f || (!!c.label_en && fold(c.label_en) === f));
      if (!hit) errors.push(t(`Plattform „${p}“ steht nicht im Katalog der Agentur (Einstellungen · Konditionen)`, `Platform “${p}” is not in the agency's catalogue (Settings · Terms)`));
      else if (!platforms.includes(hit.key)) {
        platforms.push(hit.key);
        if (hit.active === false) notes.push(t(`Plattform „${hit.label_de}“ ist in den Einstellungen abgeschaltet`, `Platform “${hit.label_de}” is switched off in the settings`));
      }
    }

    // Monate je Modell: Monatlich = Betrag je Monat, sonst Gesamtbetrag (bei mehreren Monaten gleichmäßig verteilt)
    let months: KoopImport["months"] = [];
    let budget: number | null = null;
    if (model && start && n !== undefined && fee !== undefined) {
      const single = SINGLE.has(model);
      if (single && n !== null && n > 1) errors.push(t(`${DEAL_MODEL_NAME[model].de} läuft in einem Monat; für mehrere Monate „Monatlich“ oder „Raten“ wählen`, `${DEAL_MODEL_NAME[model].de} runs in one month; for several months choose “Monatlich” or “Raten”`));
      if (!single && n === null && (model === "monthly" || model === "installments")) errors.push(t("Anzahl der Monate fehlt", "Number of months missing"));
      const count = single ? 1 : n ?? 1;
      if (!single && n === null && model !== "monthly" && model !== "installments") notes.push(t("Laufzeit 1 Monat (Spalte Monate leer)", "Duration 1 month (Months column empty)"));
      const keys = Array.from({ length: count }, (_, k) => addMonth(start, k));
      if (model === "commission_only") {
        if (fee) errors.push(t("Reine Provision hat kein Honorar; Spalte Honorar bitte leer lassen", "Commission only has no fee; please leave the Fee column empty"));
        months = keys.map((month) => ({ month, amount_cents: null, commission_forecast_cents: null, content_deadline: null }));
      } else if (NO_MONEY.has(model)) {
        if (fee) notes.push(t(`${DEAL_MODEL_NAME[model].de} wird nicht abgerechnet; das Honorar bleibt unbeachtet`, `${DEAL_MODEL_NAME[model].de} is not invoiced; the fee is ignored`));
        months = keys.map((month) => ({ month, amount_cents: null, commission_forecast_cents: null, content_deadline: null }));
      } else if (single) {
        if (fee === null && model !== "barter") errors.push(t("Honorar fehlt", "Fee missing"));
        months = [{ month: start, amount_cents: fee ?? null, commission_forecast_cents: null, content_deadline: deadline ?? null }];
        budget = fee ?? null;
      } else if (model === "monthly") {
        if (fee === null) errors.push(t("Honorar je Monat fehlt", "Fee per month missing"));
        months = keys.map((month) => ({ month, amount_cents: fee ?? null, commission_forecast_cents: null, content_deadline: null }));
        budget = fee == null ? null : fee * count;
      } else {
        if (fee === null) errors.push(t("Honorar fehlt", "Fee missing"));
        const parts = fee == null ? keys.map(() => null) : allocateEvenly(fee, count);
        months = keys.map((month, k) => ({ month, amount_cents: parts[k] ?? null, commission_forecast_cents: null, content_deadline: null }));
        budget = fee ?? null;
      }
    }

    // Stand vergangener Monate: „Stand vergangener Monate“ für Monate vor dem laufenden, „Abgerechnet bis“/„Bezahlt bis“ genauer
    const states: KoopImport["states"] = [];
    const past = parseMonthState(get("past_state"));
    if (past === undefined) errors.push(t(`Stand vergangener Monate „${get("past_state")}“: offen, abgerechnet, Marke bezahlt oder bezahlt`, `Status of past months “${get("past_state")}”: offen, abgerechnet, Marke bezahlt or bezahlt`));
    const invoicedUntil = parseMonth(get("invoiced_until"));
    const brandPaidUntil = parseMonth(get("brand_paid_until"));
    const paidUntil = parseMonth(get("paid_until"));
    if (invoicedUntil === undefined) errors.push(t(`Abgerechnet bis „${get("invoiced_until")}“ ist kein Monat`, `Invoiced until “${get("invoiced_until")}” is not a month`));
    if (brandPaidUntil === undefined) errors.push(t(`Marke bezahlt bis „${get("brand_paid_until")}“ ist kein Monat`, `Brand paid until “${get("brand_paid_until")}” is not a month`));
    if (paidUntil === undefined) errors.push(t(`Bezahlt bis „${get("paid_until")}“ ist kein Monat`, `Paid until “${get("paid_until")}” is not a month`));
    for (const u of [invoicedUntil, brandPaidUntil, paidUntil]) if (u && u > currentMonth) errors.push(t(`${monthLabel(u)} liegt in der Zukunft und kann nicht schon abgerechnet sein`, `${monthLabel(u)} is in the future and cannot already be invoiced`));
    for (const m of months) {
      const state: "invoiced" | "brand_paid" | "paid" | null = paidUntil && m.month <= paidUntil ? "paid"
        : brandPaidUntil && m.month <= brandPaidUntil ? "brand_paid"
        : invoicedUntil && m.month <= invoicedUntil ? "invoiced"
          : m.month < currentMonth && (past === "invoiced" || past === "brand_paid" || past === "paid") ? past : null;
      if (state) states.push({ month: m.month, state });
    }
    if (states.some((s) => s.state === "paid" || s.state === "brand_paid") && !ctx.canConfirmPayments) errors.push(t("Monate als bezahlt übernehmen darf nur, wer Zahlungen bestätigen darf", "Only someone who may confirm payments can take over months as paid"));
    if (states.length) notes.push(`${states.map((s) => `${monthLabel(s.month)} ${s.state === "paid" ? t("bezahlt", "paid") : s.state === "brand_paid" ? t("Marke bezahlt", "brand paid") : t("abgerechnet", "invoiced")}`).join(", ")}`);

    // Stand der Kooperation: angegeben, sonst aus dem Zeitraum
    const statusRaw = get("status");
    let status = parseDealStatus(statusRaw, ctx.labels);
    if (status === undefined) errors.push(t(`Stand „${statusRaw}“ unbekannt (z. B. In Planung, In Produktion, Abgeschlossen)`, `Status “${statusRaw}” unknown (e.g. In Planung, In Produktion, Abgeschlossen)`));
    if (status === null && months.length) {
      const last = months[months.length - 1]!.month;
      status = last < currentMonth && states.length === months.length ? "done" : start! <= currentMonth ? "production" : "planning";
      notes.push(t(`Stand „${DEAL_STATUS[status as keyof typeof DEAL_STATUS].label}“ aus dem Zeitraum`, `Status “${DEAL_STATUS[status as keyof typeof DEAL_STATUS].label}” from the period`));
    }

    // Studio: ohne den eigenen Namen (es gibt nur das eigene Profil)
    const label = [title, studio ? null : talent ? talentFullName(talent) : creatorRaw, brand?.name ?? newBrand].filter(Boolean).join(" · ") || t(`Zeile ${line}`, `Row ${line}`);
    const dupKey = talent && (brand || newBrand) && title ? `${talent.id}|${brand?.id ?? `neu:${fold(newBrand!)}`}|${fold(title)}` : null;
    const duplicate = dupKey && seen.has(dupKey) ? t("gleiche Kooperation (Creator, Marke und Titel)", "same deal (creator, brand and title)") : null;
    if (dupKey) seen.add(dupKey);
    if (newBrand && !errors.length) notes.push(t(`Marke „${newBrand}“ wird neu angelegt`, `Brand “${newBrand}” will be created`));

    const feeTerms = !studio && (ctx.fee.mode === "percent" || ctx.fee.mode === "fixed") ? ctx.fee.mode : "none";
    return {
      line, errors, duplicate, label, notes,
      values: errors.length ? null : {
        deal: {
          title, talent_id: talent!.id, ...(brand ? { brand_id: brand.id } : {}), model, status, currency: cur, budget_cents: budget,
          commission_bp: studio ? 0 : pct.bp ?? null, start_month: `${start}-01`, months: months.length,
          deliverables: get("deliverables"), platforms, usage_rights: get("usage_rights"), usage_rights_until: usageUntil ?? null,
          content_deadline: deadline ?? null, invoice_route: route, visible_to_talent: true,
        },
        terms: { agency_fee_mode: feeTerms, agency_fee_bp: feeTerms === "percent" ? ctx.fee.bp : null, agency_fee_cents: feeTerms === "fixed" ? ctx.fee.cents : null },
        months, newBrand, states, talentName: talentFullName(talent!), brandName: brand?.name ?? newBrand!,
      },
    };
  });
  return { map, rows: out, newBrands: [...new Set(out.filter((x) => x.values?.newBrand).map((x) => x.values!.newBrand!))] };
}

// ── Offene Rechnungen ──────────────────────────────────────────────────────────

export interface RechnungContext {
  today: string;
  orgCurrency: string;
  orgCountry: string | null;
  orgSmallBusiness: boolean;
  orgVatId?: string | null;
  talents: readonly TalentLite[];
  brands: readonly BrandLite[];
  deals: readonly DealLite[];
  periods: readonly PeriodLite[];
  /** vorhandene Rechnungen (Dubletten je Aussteller) */
  invoices: readonly { kind: string; talent_id: string | null; number: string | null; status: string }[];
  dunning: DunningPolicy;
  /** Feiertage der Agentur (Fristen in Werktagen) */
  holidays?: readonly string[];
  canConfirmPayments: boolean;
  canDun: boolean;
  /** Studio (Creator-Konto): das eigene Profil; Rechnungen ohne Creator und ohne Deal gehören dazu */
  selfTalent?: TalentLite | null;
}

/**
 * Arten offener Rechnungen (0064, erweitert 0080 für den direkten Weg, PRAXIS-4): Agentur an Marke, Creator an Agentur
 * (noch auszuzahlen), Provisionsrechnung der Agentur an den Creator (mahnbar) und Creator an Marke (überwachen, im Auftrag
 * mahnen).
 */
export type LegacyKind = "agency_to_brand" | "talent_to_agency" | "agency_to_talent" | "talent_to_brand";
/** Aussteller ist die Agentur */
export const legacyIssuerIsAgency = (k: LegacyKind) => k === "agency_to_brand" || k === "agency_to_talent";
/** Empfänger ist die Marke */
export const legacyToBrand = (k: LegacyKind) => k === "agency_to_brand" || k === "talent_to_brand";
/** Nutzlast für invoice_import_legacy */
export interface RechnungImport {
  kind: LegacyKind;
  number: string;
  talent_id: string | null;
  brand_id: string | null;
  deal_id: string | null;
  revenue_month: string | null;
  issue_date: string;
  due_date: string | null;
  currency: string;
  net_cents: number;
  tax_cents: number;
  gross_cents: number;
  tax_mode: string;
  notice_key: string | null;
  paid_cents: number;
  paid_on: string | null;
  dunning_stage: string | null;
  last_dunning_on: string | null;
  dunning_deadline: string | null;
  note: string | null;
}

export const LEGACY_KIND_LABEL: Record<LegacyKind, string> = {
  get agency_to_brand() { return t("an die Marke", "to the brand"); }, get talent_to_agency() { return t("Creator an die Agentur (noch auszuzahlen)", "Creator to the agency (still to be paid out)"); },
  get agency_to_talent() { return t("Provisionsrechnung an den Creator", "Commission invoice to the creator"); }, get talent_to_brand() { return t("Creator an die Marke (direkter Weg)", "Creator to the brand (direct route)"); },
};
const legacyKindHint = () => t("an Marke, Creator an Agentur, Provisionsrechnung an Creator oder Creator an Marke", "an Marke, Creator an Agentur, Provisionsrechnung an Creator or Creator an Marke");
export function parseLegacyKind(raw: string | null): LegacyKind | null | undefined {
  if (raw === null || !raw.trim()) return null;
  const f = fold(raw);
  if (["anmarke", "andiemarke", "marke", "agenturanmarke", "rechnunganmarke", "ausgang", "ausgangsrechnung", "kunde", "brand", "agencytobrand"].includes(f)) return "agency_to_brand";
  if (["creatorrechnung", "creatorandieagentur", "creatoranagentur", "creator", "talent", "eingang", "eingangsrechnung", "auszahlung", "nochauszuzahlen",
    "creatorrechnunganagentur", "creatorrechnungandieagentur", "talenttoagency"].includes(f)) return "talent_to_agency";
  // 0080: direkter Weg (Creator stellt der Marke, die Agentur stellt dem Creator ihre Provision)
  if (["provisionsrechnung", "provisionsrechnungancreator", "provisionsrechnungandencreator", "ancreator", "andencreator", "agenturancreator",
    "agenturandencreator", "rechnungancreator", "provision", "agencytotalent"].includes(f)) return "agency_to_talent";
  if (["creatoranmarke", "creatorandiemarke", "creatorrechnunganmarke", "creatorrechnungandiemarke", "direkt", "direkterweg", "direktandiemarke",
    "talenttobrand"].includes(f)) return "talent_to_brand";
  return undefined;
}

const TAX_ALIASES: Record<string, TaxMode> = {
  standard: "DOMESTIC", regelbesteuerung: "DOMESTIC", regel: "DOMESTIC", inland: "DOMESTIC", ust: "DOMESTIC", mwst: "DOMESTIC", umsatzsteuer: "DOMESTIC",
  normal: "DOMESTIC", domestic: "DOMESTIC", mitust: "DOMESTIC", mitmwst: "DOMESTIC",
  kleinunternehmer: "SMALL_BUSINESS", kleinunternehmerin: "SMALL_BUSINESS", kleinunternehmerregelung: "SMALL_BUSINESS", "19ustg": "SMALL_BUSINESS",
  kur: "SMALL_BUSINESS", nichtmwstpflichtig: "SMALL_BUSINESS", smallbusiness: "SMALL_BUSINESS", ohneust: "SMALL_BUSINESS",
  reversecharge: "REVERSE_CHARGE_EU", reversechargeeu: "REVERSE_CHARGE_EU", rceu: "REVERSE_CHARGE_EU", innergemeinschaftlich: "REVERSE_CHARGE_EU", eu: "REVERSE_CHARGE_EU",
  "13b": "REVERSE_CHARGE_DOMESTIC", "13bustg": "REVERSE_CHARGE_DOMESTIC", reversechargeinland: "REVERSE_CHARGE_DOMESTIC", inlandreversecharge: "REVERSE_CHARGE_DOMESTIC",
  bezugsteuer: "REVERSE_CHARGE_DOMESTIC", steuerschuldnerschaftdesleistungsempfaengers: "REVERSE_CHARGE_DOMESTIC",
  nichtsteuerbar: "NOT_TAXABLE_ABROAD", drittland: "NOT_TAXABLE_ABROAD", ausland: "NOT_TAXABLE_ABROAD", nichtiminlandsteuerbar: "NOT_TAXABLE_ABROAD", export: "NOT_TAXABLE_ABROAD",
  manuell: "MANUAL", manual: "MANUAL", sonstiges: "MANUAL",
};
/** Steuerfall aus der Bezeichnung (Regelbesteuerung, Kleinunternehmer, Reverse Charge EU, § 13b, nicht steuerbar, manuell) */
export function parseTaxMode(raw: string | null): TaxMode | null | undefined {
  if (raw === null || !raw.trim()) return null;
  const f = fold(raw);
  const known = (Object.keys(TAX_MODE_DB) as TaxMode[]).find((k) => fold(k) === f || fold(TAX_MODE_DB[k]) === f || fold(taxModeLabel(k)) === f);
  return known ?? TAX_ALIASES[f] ?? (/^\d+(,\d+)?%?$/.test(raw.trim().replace(/\s/g, "")) && Number(raw.trim().replace(",", ".").replace("%", "")) > 0 ? "DOMESTIC" : undefined);
}

/** Mahnstufe: 0 bzw. leer = keine, Zahl = Stufe der Mahnleiter, sonst Schlüssel oder Bezeichnung („Zahlungserinnerung“, „1. Mahnung“) */
export function parseStage(raw: string | null, policy: DunningPolicy): { key: string | null; error: string | null } {
  const v = (raw ?? "").trim();
  if (!v || ["0", "keine", "nein", "-"].includes(fold(v) || v)) return { key: null, error: null };
  const stages = policy.stages;
  if (/^\d{1,2}\.?$/.test(v)) {
    const n = Number(v.replace(".", ""));
    if (n === 0) return { key: null, error: null };
    const s = stages[n - 1];
    return s ? { key: s.key, error: null } : { key: null, error: stages.length ? t(`Mahnstufe ${n} gibt es nicht (Stufen 1 bis ${stages.length})`, `Dunning level ${n} does not exist (levels 1 to ${stages.length})`) : t("Die Mahnleiter der Agentur hat keine Stufen", "The agency's dunning levels have no levels") };
  }
  const f = fold(v);
  // Probelauf N4: eigene Bezeichnung der Mahnleiter oder gezählter Name („2. Mahnung“, wie im bisherigen System)
  const hit = stages.find((s) => fold(s.key) === f || fold(dunningStageLabel(policy, s.key)) === f
    || fold(dunningStageOrdinalLabel(policy, s.key)) === f || fold(dunningStageOrdinalLabel(policy, s.key, "en")) === f)
    ?? (["erinnerung", "zahlungserinnerung", "reminder"].includes(f) ? stages.find((s) => s.isReminder) : undefined)
    ?? (["mahnung", "erstemahnung"].includes(f) ? stages.find((s) => !s.isReminder) : undefined);
  return hit ? { key: hit.key, error: null } : { key: null, error: t(`Mahnstufe „${v}“ unbekannt (${stages.map((s, k) => `${k + 1} = ${dunningStageLabel(policy, s.key, currentLang())}`).join(", ")})`, `Dunning level “${v}” unknown (${stages.map((s, k) => `${k + 1} = ${dunningStageLabel(policy, s.key, currentLang())}`).join(", ")})`) };
}

export function rechnungRows(rows: readonly string[][], ctx: RechnungContext): { map: Record<string, number>; rows: ImportRow<RechnungImport>[] } {
  const [header = [], ...body] = rows;
  const map = bestandColumnMap("rechnungen", header);
  // Nummern je Aussteller: Agentur („org“) bzw. Creator; zurückgezogene und verworfene geben ihre Nummer frei
  const issuerKey = (kind: string, talent: string | null) => (kind === "agency_to_brand" || kind === "agency_to_talent" ? "org" : talent ?? "");
  const seen = new Set(ctx.invoices.filter((x) => x.number && !["withdrawn", "discarded"].includes(x.status))
    .map((x) => `${issuerKey(x.kind, x.talent_id)}|${normalizeInvoiceNumber(x.number!)}`));
  const out = body.map((r, i): ImportRow<RechnungImport> => {
    const get = (k: string) => (map[k] === undefined ? null : cleanText(r[map[k]!]));
    const errors: string[] = [];
    const notes: string[] = [];
    const line = i + 2;

    const number = get("number");
    if (!number) errors.push(t("Nummer fehlt", "Number missing"));
    else if (!normalizeInvoiceNumber(number)) errors.push(t(`Nummer „${number}“ enthält weder Buchstaben noch Ziffern`, `Number “${number}” contains neither letters nor digits`));
    else if (number.length > 60) errors.push(t("Nummer ist länger als 60 Zeichen", "Number is longer than 60 characters"));
    const kind = parseLegacyKind(get("kind"));
    if (kind === undefined) errors.push(t(`Art „${get("kind")}“: ${legacyKindHint()}`, `Type “${get("kind")}”: ${legacyKindHint()}`));
    const k: LegacyKind = kind ?? "agency_to_brand";

    // Creator, Marke, Kooperation
    let talent: TalentLite | null = null;
    const creatorRaw = get("creator");
    if (creatorRaw) {
      const t = findTalent(creatorRaw, ctx.talents);
      if (t.error) errors.push(t.error);
      talent = t.talent;
    }
    let brand: BrandLite | null = null;
    const brandRaw = get("brand");
    if (brandRaw) {
      brand = findBrand(brandRaw, ctx.brands);
      if (!brand) errors.push(t(`Marke „${brandRaw}“ nicht gefunden (bitte zuerst unter Marken anlegen oder importieren)`, `Brand “${brandRaw}” not found (please create or import under Brands first)`));
    }
    let deal: DealLite | null = null;
    const dealRaw = get("deal");
    if (dealRaw) {
      const f = fold(dealRaw);
      const hits = ctx.deals.filter((d) => fold(d.title) === f && (!talent || d.talent_id === talent.id) && (!brand || d.brand_id === brand.id));
      const active = hits.filter((d) => !d.archived_at);
      const pick = active.length ? active : hits;
      if (pick.length > 1) errors.push(t(`Kooperation „${dealRaw}“ ist mehrdeutig; bitte Creator und Marke angeben`, `Deal “${dealRaw}” is ambiguous; please enter creator and brand`));
      else if (!pick[0]) errors.push(t(`Kooperation „${dealRaw}“ nicht gefunden${talent || brand ? t(" (mit diesem Creator bzw. dieser Marke)", " (with this creator or brand)") : ""}`, `Deal “${dealRaw}” not found${talent || brand ? t(" (mit diesem Creator bzw. dieser Marke)", " (with this creator or brand)") : ""}`));
      else deal = pick[0];
    }
    if (deal) {
      talent = talent ?? ctx.talents.find((t) => t.id === deal!.talent_id) ?? null;
    }
    // Studio: es gibt nur einen Creator, das eigene Profil
    if (!talent && ctx.selfTalent) talent = ctx.selfTalent;
    if (deal) {
      if (legacyToBrand(k)) brand = brand ?? ctx.brands.find((b) => b.id === deal!.brand_id) ?? null;
    }
    if (legacyToBrand(k) && !brand && !errors.length) errors.push(t("Marke fehlt", "Brand missing"));
    if (k !== "agency_to_brand" && !talent && !errors.length) errors.push(t("Creator fehlt", "Creator missing"));

    // Daten
    const issue = parseDate(get("issue_date"));
    if (issue === undefined) errors.push(t(`Rechnungsdatum „${get("issue_date")}“ ist kein Datum`, `Invoice date “${get("issue_date")}” is not a date`));
    else if (issue === null) errors.push(t("Rechnungsdatum fehlt", "Invoice date missing"));
    else if (issue > ctx.today) errors.push(t("Rechnungsdatum liegt in der Zukunft", "Invoice date is in the future"));
    // Fälligkeit als Datum oder als Zahlungsziel in Tagen
    const dueRaw = get("due_date");
    let due = parseDate(dueRaw);
    const dueDays = dueRaw && /^\d{1,3}(\s*tage?)?$/i.test(dueRaw) ? Number(dueRaw.replace(/\D+$/, "")) : null;
    if (dueDays !== null && issue) { due = addDaysByUnit(issue, dueDays, "calendar"); notes.push(t(`fällig ${dueDays} Tage nach Rechnungsdatum`, `due ${dueDays} days after the invoice date`)); }
    if (due === undefined) errors.push(t(`Fälligkeit „${dueRaw}“ ist kein Datum`, `Due date “${dueRaw}” is not a date`));
    else if (due && issue && due < issue) errors.push(t("Fälligkeit liegt vor dem Rechnungsdatum", "Due date is before the invoice date"));
    const month = parseMonth(get("revenue_month"));
    if (month === undefined) errors.push(t(`Leistungsmonat „${get("revenue_month")}“ ist kein Monat (MM.JJJJ)`, `Service month “${get("revenue_month")}” is not a month (MM.YYYY)`));
    if (deal && month && !ctx.periods.some((p) => p.deal_id === deal!.id && p.month.slice(0, 7) === month)) notes.push(t(`${monthLabel(month)} ist kein Monat der Kooperation; die Rechnung hängt an der Kooperation ohne Monat`, `${monthLabel(month)} is not a month of the deal; the invoice is attached to the deal without a month`));

    // Beträge: zwei von drei genügen
    let net = parseMoney(get("net"));
    let tax = parseMoney(get("tax"));
    let gross = parseMoney(get("gross"));
    if (net === undefined) errors.push(t(`Netto „${get("net")}“ ist kein gültiger Betrag`, `Net “${get("net")}” is not a valid amount`));
    if (tax === undefined) errors.push(t(`Steuer „${get("tax")}“ ist kein gültiger Betrag`, `Tax “${get("tax")}” is not a valid amount`));
    if (gross === undefined) errors.push(t(`Brutto „${get("gross")}“ ist kein gültiger Betrag`, `Gross “${get("gross")}” is not a valid amount`));
    if (net !== undefined && tax !== undefined && gross !== undefined) {
      if (net == null && gross != null) net = gross - (tax ?? 0);
      if (tax == null && net != null && gross != null) tax = gross - net;
      if (gross == null && net != null) gross = net + (tax ?? 0);
      tax = tax ?? 0;
      if (net == null || gross == null) errors.push(t("Netto bzw. Brutto fehlt", "Net or gross missing"));
      else if (net + tax !== gross) errors.push(t("Netto plus Steuer ergibt nicht den Bruttobetrag", "Net plus tax does not add up to the gross amount"));
      else if (net <= 0 || tax < 0) errors.push(t("Betrag muss größer 0 sein (Stornos und Gutschriften bleiben im bisherigen System)", "Amount must be greater than 0 (cancellations and credits stay in the previous system)"));
    }
    const cur = parseCurrency(get("currency")) ?? (get("currency") ? undefined : (deal?.currency ?? ctx.orgCurrency) as Currency);
    if (cur === undefined) errors.push(t(`Währung „${get("currency")}“ unbekannt`, `Currency “${get("currency")}” unknown`));
    else if (deal && deal.currency !== cur) errors.push(t(`Die Kooperation rechnet in ${deal.currency}, die Rechnung in ${cur}`, `The deal is in ${deal.currency}, the invoice in ${cur}`));

    // Steuerfall: angegeben, sonst bei Steuer Regelbesteuerung, ohne Steuer aus Land und Status der Beteiligten
    let mode = parseTaxMode(get("tax_mode"));
    if (mode === undefined) errors.push(t(`Steuerfall „${get("tax_mode")}“ unbekannt (Regelbesteuerung, Kleinunternehmer, Reverse Charge EU, § 13b, nicht steuerbar, manuell)`, `Tax treatment “${get("tax_mode")}” unknown (Regelbesteuerung, Kleinunternehmer, Reverse Charge EU, § 13b, nicht steuerbar, manuell)`));
    const issuerCountry = legacyIssuerIsAgency(k) ? ctx.orgCountry : talent?.country ?? null;
    const recipientCountry = legacyToBrand(k) ? brand?.country ?? null : k === "agency_to_talent" ? talent?.country ?? null : ctx.orgCountry;
    if (mode === null && typeof tax === "number" && typeof gross === "number" && cur) {
      if (tax > 0) mode = "DOMESTIC";
      else {
        const tl = determineTaxTreatment({
          issuerCountry, issuerSmallBusiness: legacyIssuerIsAgency(k) ? ctx.orgSmallBusiness : !!talent?.small_business, issuerVatId: legacyIssuerIsAgency(k) ? ctx.orgVatId ?? null : talent?.vat_id ?? null,
          recipientCountry, recipientIsBusiness: legacyToBrand(k) ? brand?.is_business !== false : true,
          recipientVatId: legacyToBrand(k) ? brand?.vat_id ?? null : k === "agency_to_talent" ? talent?.vat_id ?? null : ctx.orgVatId ?? null,
          currency: cur, grossCents: gross,
        });
        if (tl.mode !== "DOMESTIC" && tl.mode !== "MANUAL" && tl.ratePercent === 0) {
          mode = tl.mode;
          notes.push(t(`Steuerfall ermittelt: ${taxModeLabel(tl.mode, currentLang())}`, `Tax treatment determined: ${taxModeLabel(tl.mode, currentLang())}`));
        } else errors.push(t("Steuerfall fehlt (ohne Steuer z. B. Kleinunternehmer, Reverse Charge EU, § 13b oder nicht steuerbar)", "Tax treatment missing (without tax e.g. Kleinunternehmer, Reverse Charge EU, § 13b or nicht steuerbar)"));
      }
    }
    if (mode && mode !== "DOMESTIC" && mode !== "MANUAL" && typeof tax === "number" && tax > 0) errors.push(t(`Mit „${taxModeLabel(mode, currentLang())}“ weist die Rechnung keine Steuer aus`, `With “${taxModeLabel(mode, currentLang())}” the invoice shows no tax`));
    const notice = mode && mode !== "MANUAL" ? forcedTaxTreatment(mode, { issuerCountry, recipientCountry }).noticeKey : null;

    // Bereits bezahlt: Betrag oder „ja“/„voll“
    const paidRaw = get("paid");
    const paidWord = paidRaw ? fold(paidRaw) : "";
    let paid: number | null | undefined = ["ja", "voll", "vollstaendig", "komplett", "x", "bezahlt"].includes(paidWord) ? (typeof gross === "number" ? gross : null)
      : ["nein", "offen", "0"].includes(paidWord) ? 0 : parseMoney(paidRaw);
    if (paid === undefined) errors.push(t(`Bereits bezahlt „${paidRaw}“ ist kein gültiger Betrag`, `Already paid “${paidRaw}” is not a valid amount`));
    paid = paid ?? 0;
    if (typeof gross === "number" && paid > gross) errors.push(t("Bereits bezahlt ist mehr als der Bruttobetrag", "Already paid is more than the gross amount"));
    if (paid > 0 && !ctx.canConfirmPayments) errors.push(t("Bezahlte Beträge übernehmen darf nur, wer Zahlungen bestätigen darf", "Only someone who may confirm payments can take over paid amounts"));
    const paidOn = parseDate(get("paid_on"));
    if (paidOn === undefined) errors.push(t(`Bezahlt am „${get("paid_on")}“ ist kein Datum`, `Paid on “${get("paid_on")}” is not a date`));
    else if (paidOn && (paidOn > ctx.today || (issue && paidOn < issue))) errors.push(t("Bezahlt am liegt zwischen Rechnungsdatum und heute", "Paid on must be between the invoice date and today"));
    if (paid > 0 && !paidOn) notes.push(t("Zahlungsdatum: heute (Spalte „Bezahlt am“ leer)", "Payment date: today (column “Bezahlt am” empty)"));
    if (typeof gross === "number" && paid >= gross && paid > 0) notes.push(t("voll bezahlt", "fully paid"));

    // Mahnstufe und letzte Mahnung; Frist wie beim Mahnen (Werk- bzw. Kalendertage der Stufe)
    const stage = parseStage(get("stage"), ctx.dunning);
    if (stage.error) errors.push(stage.error);
    const last = parseDate(get("last_dunning"));
    if (last === undefined) errors.push(t(`Letzte Mahnung „${get("last_dunning")}“ ist kein Datum`, `Last dunning notice “${get("last_dunning")}” is not a date`));
    let deadline: string | null = null;
    if (stage.key) {
      if (k === "talent_to_agency") errors.push(t("Rechnungen eines Creators an die Agentur werden nicht gemahnt", "A creator's invoices to the agency are not dunned"));
      if (typeof gross === "number" && paid >= gross) errors.push(t("Eine bezahlte Rechnung hat keine Mahnstufe", "A paid invoice has no dunning level"));
      if (!ctx.canDun) errors.push(t("Mahnstufen übernehmen darf nur, wer mahnen darf", "Only someone who may send dunning notices can take over dunning levels"));
      if (!last) errors.push(t("Zur Mahnstufe fehlt das Datum der letzten Mahnung", "The date of the last dunning notice is missing for the dunning level"));
      else if (last > ctx.today || (issue && last < issue)) errors.push(t("Letzte Mahnung liegt zwischen Rechnungsdatum und heute", "Last dunning notice must be between the invoice date and today"));
      else {
        const s = ctx.dunning.stages.find((x) => x.key === stage.key)!;
        deadline = addDaysByUnit(last, s.deadline.days, s.deadline.unit, ctx.holidays ?? []);
        notes.push(t(`${dunningStageLabel(ctx.dunning, stage.key, currentLang())} vom ${last.split("-").reverse().join(".")}, Frist bis ${deadline.split("-").reverse().join(".")}`, `${dunningStageLabel(ctx.dunning, stage.key, currentLang())} of ${last.split("-").reverse().join("/")}, deadline until ${deadline.split("-").reverse().join("/")}`));
      }
    } else if (last) errors.push(t("Zum Datum der letzten Mahnung fehlt die Mahnstufe (1 = erste Stufe der Mahnleiter)", "The dunning level is missing for the date of the last dunning notice (1 = first level of the dunning process)"));

    const note = get("note");
    if (note && note.length > 500) errors.push(t("Bemerkung ist länger als 500 Zeichen", "Remark is longer than 500 characters"));

    // Dublette: gleiche Nummer beim selben Aussteller (auch anders geschrieben, „RE 2026 7“ = „RE-2026-007“)
    const dupKey = number && normalizeInvoiceNumber(number) && (legacyIssuerIsAgency(k) || talent) ? `${issuerKey(k, talent?.id ?? null)}|${normalizeInvoiceNumber(number)}` : null;
    const duplicate = dupKey && seen.has(dupKey) ? (legacyIssuerIsAgency(k) ? t("Nummer gibt es schon bei den Rechnungen der Agentur", "Number already exists among the agency's invoices") : t("Nummer gibt es bei diesem Creator schon", "Number already exists for this creator")) : null;
    if (dupKey) seen.add(dupKey);

    const creatorName = talent ? talentFullName(talent) : creatorRaw;
    const who = k === "agency_to_brand" ? brand?.name ?? brandRaw : k === "talent_to_brand" ? [creatorName, brand?.name ?? brandRaw].filter(Boolean).join(" an ") : creatorName;
    return {
      line, errors, duplicate, notes, label: [number, who].filter(Boolean).join(" · ") || t(`Zeile ${line}`, `Row ${line}`),
      values: errors.length ? null : {
        kind: k, number: number!, talent_id: talent?.id ?? null, brand_id: legacyToBrand(k) ? brand?.id ?? null : null, deal_id: deal?.id ?? null,
        revenue_month: month ?? null, issue_date: issue!, due_date: due ?? null, currency: cur!, net_cents: net!, tax_cents: tax as number, gross_cents: gross!,
        tax_mode: TAX_MODE_DB[mode!], notice_key: notice && notice !== "MANUAL_REVIEW" ? notice : null, paid_cents: paid, paid_on: paid > 0 ? paidOn ?? null : null,
        dunning_stage: stage.key, last_dunning_on: stage.key ? last ?? null : null, dunning_deadline: deadline, note,
      },
    };
  });
  return { map, rows: out };
}

// ── Kontexte aus den geladenen Daten (Seite und Tests nutzen dieselben) ─────────

/** Kontext der Kooperationen aus den Daten der Kooperationsliste (ladeKooperationen) */
/** Studio: das eigene Profil (im Creator-Konto gibt es genau einen Creator, talents.is_self) */
export function eigenesProfil(talents: readonly TalentLite[]): TalentLite | null {
  return talents.find((x) => x.status !== "archived") ?? talents[0] ?? null;
}

export function koopContext(d: KoopDaten, a: { today: string; orgCurrency: string; canConfirmPayments: boolean; studio?: boolean }): KoopContext {
  const s = d.settings;
  const cur = s?.currency ?? a.orgCurrency;
  return {
    today: a.today, orgCurrency: cur, currencies: s?.currencies?.length ? s.currencies : [cur],
    talents: d.talents as unknown as TalentLite[], brands: d.brands as unknown as BrandLite[], deals: d.deals as unknown as DealLite[],
    models: d.models, labels: labelMap(d.labels), platforms: d.platforms,
    fee: { mode: s?.agency_fee_mode ?? null, bp: s?.agency_fee_bp ?? null, cents: s?.agency_fee_cents ?? null },
    canConfirmPayments: a.canConfirmPayments,
    selfTalent: a.studio ? eigenesProfil(d.talents as unknown as TalentLite[]) : null,
  };
}

/** Kontext der offenen Rechnungen aus Stammdaten, Kooperationen, Rechnungen, Einstellungen und Mahnleiter der Agentur */
export function rechnungContext(q: {
  talents: readonly unknown[]; brands: readonly unknown[]; deals: readonly unknown[]; periods: readonly PeriodLite[];
  invoices: readonly { kind: string; talent_id: string | null; number: string | null; status: string }[];
  settings: Record<string, unknown> | null; dunning: DunningPolicy;
}, a: { today: string; orgCurrency: string; orgCountry: string | null; canConfirmPayments: boolean; canDun: boolean; studio?: boolean }): RechnungContext {
  const s = q.settings ?? {};
  const region = typeof s.holiday_region === "string" ? s.holiday_region : null;
  const year = Number(a.today.slice(0, 4));
  return {
    today: a.today, orgCurrency: (s.currency as string | undefined) ?? a.orgCurrency, orgCountry: (s.country as string | null | undefined) ?? a.orgCountry,
    orgSmallBusiness: !!s.small_business, orgVatId: (s.vat_id as string | null | undefined) ?? null,
    talents: q.talents as TalentLite[], brands: q.brands as BrandLite[], deals: q.deals as DealLite[], periods: q.periods, invoices: q.invoices,
    dunning: q.dunning, holidays: region ? holidayDates(region, [year - 1, year, year + 1]) : [], canConfirmPayments: a.canConfirmPayments, canDun: a.canDun,
    selfTalent: a.studio ? eigenesProfil(q.talents as TalentLite[]) : null,
  };
}
